import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { selectBrowserThreadState, useBrowserStateStore } from "../browserStateStore";
import { threadHasStarted } from "../components/ChatView.logic";
import { finalizePromotedDraftThreadByRef, useComposerDraftStore } from "../composerDraftStore";
import type { DiffRouteSearch } from "../diffRouteSearch";
import { retainThreadDetailSubscription } from "../environments/runtime/service";
import { useRightPanelStore } from "../rightPanelStore";
import type { RightPanelKind } from "../rightPanelSurface";
import { selectThreadExistsByRef, useStore } from "../store";
import { createThreadSelectorByRef } from "../storeSelectors";
import { buildThreadRouteParams } from "../threadRoutes";
import { useRightPanelState } from "../useRightPanel";
import { resolveRightPanelRenderState } from "./rightPanelRenderState";
import { useDiffPanelMountState } from "./-useDiffPanelMountState";

export function useLocalThreadRouteState(threadRef: ScopedThreadRef | null) {
  const serverThread = useStore(useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]));
  const threadExists = useStore((store) => selectThreadExistsByRef(store, threadRef));
  const draftThread = useComposerDraftStore((store) => {
    if (!threadRef) return null;
    return store.getDraftThreadByRef(threadRef);
  });

  return {
    draftThread,
    routeThreadExists: threadExists || draftThread !== null,
    serverThreadStarted: threadHasStarted(serverThread),
  };
}

export function useThreadRightPanelState(
  threadRef: ScopedThreadRef | null,
  search: DiffRouteSearch,
) {
  const panelKey = threadRef ? scopedThreadKey(threadRef) : null;
  const panel = resolveRightPanelRenderState(useRightPanelState(panelKey), search);
  const toggleRightPanelMaximized = useCallback(() => {
    if (!threadRef) return;
    useRightPanelStore.getState().toggleMaximized(scopedThreadKey(threadRef));
  }, [threadRef]);

  return {
    ...panel,
    currentThreadKey: threadRef ? `${threadRef.environmentId}:${threadRef.threadId}` : null,
    toggleRightPanelMaximized,
  };
}

export function useBrowserThreadRouteState(threadRef: ScopedThreadRef | null) {
  const browserThreadState = useBrowserStateStore((state) =>
    selectBrowserThreadState(state.browserStateByThreadKey, threadRef),
  );
  const pendingAutoReveal = useBrowserStateStore((state) => {
    if (!threadRef) return false;
    return state.pendingAutoRevealKeys.has(`${threadRef.environmentId}:${threadRef.threadId}`);
  });
  const consumeAutoReveal = useBrowserStateStore((state) => state.consumeAutoReveal);
  return { browserThreadState, pendingAutoReveal, consumeAutoReveal };
}

function useRightPanelNavigation(input: {
  readonly autoRevealedPreviewThreadKeysRef: { current: Set<string> };
  readonly markDiffOpened: () => void;
  readonly threadRef: ScopedThreadRef | null;
}) {
  const { autoRevealedPreviewThreadKeysRef, markDiffOpened, threadRef } = input;
  const closeRightPanel = useCallback(() => {
    if (!threadRef) return;
    const threadKey = scopedThreadKey(threadRef);
    autoRevealedPreviewThreadKeysRef.current.add(threadKey);
    useRightPanelStore.getState().close(threadKey);
  }, [autoRevealedPreviewThreadKeysRef, threadRef]);
  const selectRightPanel = useCallback(
    (kind: RightPanelKind) => {
      if (!threadRef) return;
      const threadKey = scopedThreadKey(threadRef);
      const store = useRightPanelStore.getState();
      if (kind === "diff") {
        markDiffOpened();
        store.openDiff(threadKey);
        return;
      }
      if (kind === "preview") {
        store.revealBrowser(threadKey);
        return;
      }
      if (kind === "file" || kind === "pull-request") return;
      store.open(threadKey, kind);
    },
    [markDiffOpened, threadRef],
  );
  const activateRightPanelSurface = useCallback(
    (surfaceId: string) => {
      if (!threadRef) return;
      useRightPanelStore.getState().activateSurface(scopedThreadKey(threadRef), surfaceId);
    },
    [threadRef],
  );
  const closeRightPanelSurfaceTab = useCallback(
    (surfaceId: string) => {
      if (!threadRef) return;
      const threadKey = scopedThreadKey(threadRef);
      if (surfaceId.startsWith("browser:")) {
        autoRevealedPreviewThreadKeysRef.current.add(threadKey);
      }
      useRightPanelStore.getState().closeSurface(threadKey, surfaceId);
    },
    [autoRevealedPreviewThreadKeysRef, threadRef],
  );

  return {
    closeRightPanel,
    closeRightPanelSurfaceTab,
    selectRightPanel,
    activateRightPanelSurface,
  };
}

function useThreadRouteLifecycle(input: {
  readonly draftPromoted: boolean;
  readonly environmentIdParam: string;
  readonly serverThreadStarted: boolean;
  readonly threadIdParam: string;
  readonly threadRef: ScopedThreadRef | null;
}): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (
      !input.threadRef ||
      input.threadRef.environmentId === input.environmentIdParam ||
      input.threadRef.threadId !== input.threadIdParam
    ) {
      return;
    }
    void navigate({
      to: "/$environmentId/$threadId",
      params: buildThreadRouteParams(input.threadRef),
      replace: true,
      search: (previous) => previous,
    });
  }, [input.environmentIdParam, input.threadIdParam, input.threadRef, navigate]);

  useEffect(() => {
    if (!input.threadRef) return;
    return retainThreadDetailSubscription(input.threadRef.environmentId, input.threadRef.threadId);
  }, [input.threadRef]);

  useEffect(() => {
    if (!input.threadRef || !input.serverThreadStarted || !input.draftPromoted) return;
    finalizePromotedDraftThreadByRef(input.threadRef);
  }, [input.draftPromoted, input.serverThreadStarted, input.threadRef]);
}

function useBrowserPreviewAutoReveal(input: {
  readonly autoRevealedPreviewThreadKeysRef: { current: Set<string> };
  readonly consumeBrowserPreviewAutoReveal: (threadKey: string) => void;
  readonly pendingBrowserPreviewAutoReveal: boolean;
  readonly routeThreadExists: boolean;
  readonly threadRef: ScopedThreadRef | null;
}): void {
  const {
    autoRevealedPreviewThreadKeysRef,
    consumeBrowserPreviewAutoReveal,
    pendingBrowserPreviewAutoReveal,
    routeThreadExists,
    threadRef,
  } = input;
  useEffect(() => {
    if (!pendingBrowserPreviewAutoReveal || !threadRef || !routeThreadExists) {
      return;
    }
    const threadKey = `${threadRef.environmentId}:${threadRef.threadId}`;
    consumeBrowserPreviewAutoReveal(threadKey);
    if (autoRevealedPreviewThreadKeysRef.current.has(threadKey)) return;
    autoRevealedPreviewThreadKeysRef.current.add(threadKey);
    useRightPanelStore.getState().revealBrowser(threadKey);
  }, [
    autoRevealedPreviewThreadKeysRef,
    consumeBrowserPreviewAutoReveal,
    pendingBrowserPreviewAutoReveal,
    routeThreadExists,
    threadRef,
  ]);
}

export function useThreadRouteWiring(input: {
  readonly threadRef: ScopedThreadRef | null;
  readonly currentThreadKey: string | null;
  readonly diffOpen: boolean;
  readonly draftPromoted: boolean;
  readonly environmentIdParam: string;
  readonly threadIdParam: string;
  readonly serverThreadStarted: boolean;
  readonly routeThreadExists: boolean;
  readonly pendingBrowserPreviewAutoReveal: boolean;
  readonly consumeBrowserPreviewAutoReveal: (threadKey: string) => void;
}) {
  const autoRevealedPreviewThreadKeysRef = useRef(new Set<string>());
  const { hasOpenedDiff, markDiffOpened } = useDiffPanelMountState({
    currentThreadKey: input.currentThreadKey,
    diffOpen: input.diffOpen,
  });
  const panelNavigation = useRightPanelNavigation({
    autoRevealedPreviewThreadKeysRef,
    markDiffOpened,
    threadRef: input.threadRef,
  });

  useThreadRouteLifecycle(input);
  useBrowserPreviewAutoReveal({
    autoRevealedPreviewThreadKeysRef,
    consumeBrowserPreviewAutoReveal: input.consumeBrowserPreviewAutoReveal,
    pendingBrowserPreviewAutoReveal: input.pendingBrowserPreviewAutoReveal,
    routeThreadExists: input.routeThreadExists,
    threadRef: input.threadRef,
  });

  return { ...panelNavigation, hasOpenedDiff, markDiffOpened };
}
